import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* USER DEFINED COMPONENTS */
import ServiceForm from "./service-form";

/* API IMPORT */
import { editServiceById, getServiceById } from "../../../service/api";

const validationSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  shortDescription: yup.string().required("Short description is required"),
  description: yup
    .string()
    .test(
      "not-empty",
      "Description is required",
      (value) => value && value.replace(/<(.|\n)*?>/g, "").trim().length > 0
    )
    .required("Description is required"),
});

const EditService = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      title: "",
      slug: "",
      shortDescription: "",
      description: "",
      icon: "Pill",
      sortOrder: "",
      seoTitle: "",
      seoDescription: "",
      isActive: true,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const body = {
        title: values.title,
        slug: values.slug,
        shortDescription: values.shortDescription,
        description: values.description,
        icon: values.icon,
        seoTitle: values.seoTitle,
        seoDescription: values.seoDescription,
        isActive: values.isActive,
      };
      if (values.sortOrder !== "" && values.sortOrder !== null) {
        body.sortOrder = Number(values.sortOrder);
      }

      setLoading(true);
      editServiceById(id, body)
        .then((res) => {
          if (res?.status) {
            toast.success(res?.message);
            navigate("/services");
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          toast.error(
            error?.response?.data?.message || "Failed to update the service"
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  /* USE-EFFECTS */
  useEffect(() => {
    getServiceById(id)
      .then((res) => {
        if (res?.status && res?.data) {
          formik.setValues({
            title: res.data.title || "",
            slug: res.data.slug || "",
            shortDescription: res.data.shortDescription || "",
            description: res.data.description || "",
            icon: res.data.icon || "Pill",
            sortOrder: res.data.sortOrder ?? "",
            seoTitle: res.data.seoTitle || "",
            seoDescription: res.data.seoDescription || "",
            isActive: res.data.isActive ?? true,
          });
        } else {
          toast.error(res?.message);
        }
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message || "Failed to load the service");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <section className="p-5 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Edit Service | WellCare Pharmacy</title>
      </Helmet>

      <form onSubmit={formik.handleSubmit}>
        <div className="container-full-width">
          <div className="block xl:flex lg:flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
                Edit Service
              </h1>
              <h2 className="text-base xl:text-lg lg:text-lg font-ibm-regular text-greyText tracking-tight">
                Update the details of this service.
              </h2>
            </div>
            <div className="flex items-center gap-2 mt-2 xl:mt-0 lg:mt-0">
              <Link to="/services">
                <Button className="w-full xl:w-auto lg:w-auto whitespace-nowrap shadow-none hover:shadow-none bg-white hover:bg-black hover:bg-opacity-5 px-5 py-3 rounded-md border border-authBorder text-sm font-ibm-semibold text-black normal-case transition-all duration-300 ease-in-out">
                  Back to Services
                </Button>
              </Link>
              <Button
                type="submit"
                disabled={loading}
                className="w-full xl:w-auto lg:w-auto whitespace-nowrap shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md border border-logoYellow text-sm font-ibm-semibold text-white normal-case transition-all duration-300 ease-in-out"
              >
                {loading ? "Please wait..." : "Save Changes"}
              </Button>
            </div>
          </div>

          <div className="mt-6">
            <ServiceForm formik={formik} />
          </div>
        </div>
      </form>
    </section>
  );
};

export default EditService;
